import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

// lazyWithRetry — chunk-failure-tolerant React.lazy.
//
// Every production build content-hashes its chunks. A browser tab opened
// BEFORE a rebuild still holds the old hash map, so the first lazy import
// after a deploy can request a chunk that no longer exists — the dynamic
// import rejects and the nearest error boundary shows a crash page even
// though nothing is actually wrong. The fix is the standard one: on import
// failure, force ONE full reload (which fetches the fresh index.html and
// hash map), guarded per chunk via sessionStorage so a genuinely broken
// chunk still surfaces as an error instead of a reload loop.
//
// Use this instead of React.lazy for every code-split surface (client
// actions, settings, debug pages).

const RELOAD_FLAG_PREFIX = "ede.chunk-reload:";

// isChunkLoadError — true when `error` is a failed dynamic-import / code-split
// chunk fetch rather than a genuine runtime error inside the module. After a
// deploy, a tab holding the old hash map requests a chunk that no longer
// exists; the browser rejects the import with one of these stable messages.
// Shared by lazyWithRetry (decide whether to auto-reload) and the route /
// client-action error UIs (show "a new version is available" instead of a
// raw stack). Kept deliberately broad — false positives only cost one extra
// reload, false negatives strand the user on a crash page.
const _CHUNK_ERROR_PATTERNS = [
    "failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "importing a module script failed",       // Safari
    "unable to preload css",                   // Vite CSS preload
    "failed to load module script",
];

export function isChunkLoadError(error: unknown): boolean {
    if (!error) return false;
    if (error instanceof Error && error.name === "ChunkLoadError") return true;
    const message = (error instanceof Error ? error.message : String(error)).toLowerCase();
    return _CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function readFlag(key: string): boolean {
    try {
        return window.sessionStorage.getItem(RELOAD_FLAG_PREFIX + key) === "1";
    } catch {
        return true; // storage unavailable → never auto-reload (avoid loops)
    }
}

function writeFlag(key: string, on: boolean): void {
    try {
        if (on) window.sessionStorage.setItem(RELOAD_FLAG_PREFIX + key, "1");
        else window.sessionStorage.removeItem(RELOAD_FLAG_PREFIX + key);
    } catch {
        // storage unavailable — nothing to record
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy's own constraint
export function lazyWithRetry<T extends ComponentType<any>>(
    importer: () => Promise<{ default: T }>,
    chunkKey: string,
): LazyExoticComponent<T> {
    return lazy(async () => {
        try {
            const mod = await importer();
            writeFlag(chunkKey, false); // healthy again — re-arm the guard
            return mod;
        } catch (error) {
            // A genuine runtime error inside the module (not a missing chunk)
            // must surface immediately — reloading would not fix it and only
            // loops the user. Let the nearest error boundary render it.
            if (!isChunkLoadError(error)) {
                throw error;
            }
            if (readFlag(chunkKey)) {
                throw error; // already reloaded once for this chunk — real failure
            }
            writeFlag(chunkKey, true);
            window.location.reload();
            // Suspend forever while the reload happens so nothing flashes.
            return await new Promise<never>(() => undefined);
        }
    });
}
